import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, TrendingDown, DollarSign, AlertTriangle, Info } from 'lucide-react';
import { useAppStore } from '../store';
import { getPipSize, getPipValuePerLot, getRatesFromPrices } from '../utils/lotCalculatorUtils';

export default function LotSizeCalculator() {
  const { prices, selectedPair } = useAppStore();
  const [accountBalance, setAccountBalance] = useState(10000);
  const [riskPercentage, setRiskPercentage] = useState(1);
  const [stopLossPips, setStopLossPips] = useState(20);
  const [entryPrice, setEntryPrice] = useState(0);
  const [stopLossPrice, setStopLossPrice] = useState(0);
  const [direction, setDirection] = useState('long');
  const [symbol, setSymbol] = useState(selectedPair || 'EURUSD');
  const [lotSize, setLotSize] = useState(0);
  const [riskAmount, setRiskAmount] = useState(0);
  const [pipValue, setPipValue] = useState(0);
  const [positionSize, setPositionSize] = useState(0);

  // Get current price when symbol changes
  useEffect(() => {
    if (selectedPair && prices) {
      const priceData = prices.find(p => p.symbol === selectedPair);
      if (priceData) {
        setSymbol(selectedPair);
        setEntryPrice(priceData.bid || priceData.ask || 0);
        if (selectedPair.includes('JPY')) {
          setStopLossPips(30);
        } else if (selectedPair.includes('XAU')) {
          setStopLossPips(50);
        } else if (selectedPair.includes('XAG')) {
          setStopLossPips(50);
        } else if (selectedPair.includes('US30') || selectedPair.includes('US100') || selectedPair.includes('US500') || selectedPair.includes('NAS100') || selectedPair.includes('SPX')) {
          setStopLossPips(100);
        } else {
          setStopLossPips(20);
        }
      }
    }
  }, [selectedPair, prices]);

  // Pip value: stessa logica della mobile-app (JPY, USD-base, cross, metalli, indici)
  useEffect(() => {
    if (symbol && entryPrice && entryPrice > 0) {
      const { usdJpyRate, quoteToUsdRate } = getRatesFromPrices(prices, symbol);
      const symNorm = (symbol || '').toUpperCase().replace(/\.+$/, '');
      const isJpyCross = symNorm.includes('JPY') && symNorm !== 'USDJPY';
      const isCross = symNorm.length === 6 && !symNorm.endsWith('USD') && !symNorm.startsWith('USD') && !symNorm.includes('JPY');
      const pipValuePerLot = getPipValuePerLot(
        symbol,
        entryPrice,
        isJpyCross ? usdJpyRate : undefined,
        isCross ? quoteToUsdRate : undefined
      );
      setPipValue(pipValuePerLot);
    } else {
      setPipValue(0);
    }
  }, [symbol, entryPrice, prices]);

  // Calculate lot size
  useEffect(() => {
    if (accountBalance && riskPercentage && stopLossPips && pipValue && entryPrice && entryPrice > 0) {
      // Calculate risk amount
      const riskAmountCalc = (accountBalance * riskPercentage) / 100;
      setRiskAmount(riskAmountCalc);

      // Calculate lot size: Risk Amount / (Stop Loss Pips * Pip Value per Lot)
      if (pipValue > 0 && stopLossPips > 0) {
        const lotSizeCalc = riskAmountCalc / (stopLossPips * pipValue);
        const clampedLotSize = Math.max(0.01, Math.min(100, lotSizeCalc));
        setLotSize(clampedLotSize);
        
        // Calculate position size in units/contracts
        let positionSizeCalc = 0;
        if (symbol.includes('XAU')) {
          // Gold: 1 lot = 100 oz
          positionSizeCalc = clampedLotSize * 100;
        } else if (symbol.includes('XAG')) {
          // Silver: 1 lot = 5000 oz
          positionSizeCalc = clampedLotSize * 5000;
        } else if (symbol.includes('US30') || symbol.includes('US100') || symbol.includes('US500') || symbol.includes('NAS100') || symbol.includes('SPX')) {
          // Indices: 1 lot = 1 contract
          positionSizeCalc = clampedLotSize;
        } else {
          // Forex: 1 lot = 100,000 units
          positionSizeCalc = clampedLotSize * 100000;
        }
        setPositionSize(positionSizeCalc);
      } else {
        setLotSize(0);
        setPositionSize(0);
      }
    } else {
      setLotSize(0);
      setRiskAmount(0);
      setPositionSize(0);
    }
  }, [accountBalance, riskPercentage, stopLossPips, pipValue, entryPrice, symbol]);

  // Update stop loss price when entry price or pips change
  useEffect(() => {
    if (entryPrice && stopLossPips && entryPrice > 0) {
      const pipSize = getPipSize(symbol);

      if (direction === 'long') {
        setStopLossPrice(entryPrice - (stopLossPips * pipSize));
      } else {
        setStopLossPrice(entryPrice + (stopLossPips * pipSize));
      }
    }
  }, [entryPrice, stopLossPips, direction, symbol]);

  // Get available symbols from prices
  const availableSymbols = prices?.map(p => p.symbol) || ['EURUSD', 'GBPUSD', 'USDJPY', 'XAUUSD'];

  const formatNumber = (num, decimals = 2) => {
    return num.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  return (
    <div className="space-y-4">
      <div className="glass-card p-5 sm:p-7">
        <div className="flex items-center gap-3.5 mb-7">
          <div className="p-2.5 bg-gradient-to-br from-violet-500/30 to-purple-600/30 rounded-xl shadow-medium shadow-violet-500/20">
            <Calculator className="w-6 h-6 text-violet-300" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white tracking-tight mb-1">Lot Size Calculator</h2>
            <p className="text-sm text-slate-300 leading-relaxed">Calcola la dimensione del lotto in base al rischio</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Column - Inputs */}
          <div className="space-y-4">
            {/* Account Balance */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Account Balance ($)
              </label>
              <input
                type="number"
                value={accountBalance}
                onChange={(e) => setAccountBalance(parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-3 bg-slate-800/60 border border-slate-700/60 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 transition-smooth placeholder:text-slate-500"
                placeholder="10000"
                min="0"
                step="100"
              />
            </div>

            {/* Risk Percentage */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Risk per Trade (%)
              </label>
              <input
                type="number"
                value={riskPercentage}
                onChange={(e) => setRiskPercentage(parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-3 bg-slate-800/60 border border-slate-700/60 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 transition-smooth placeholder:text-slate-500"
                placeholder="1"
                min="0.1"
                max="10"
                step="0.1"
              />
              <p className="text-xs text-slate-500 mt-1">Raccomandato: 1-2%</p>
            </div>

            {/* Symbol Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Symbol
              </label>
              <select
                value={symbol}
                onChange={(e) => {
                  setSymbol(e.target.value);
                  const priceData = prices?.find(p => p.symbol === e.target.value);
                  if (priceData) {
                    setEntryPrice(priceData.bid || priceData.ask || 0);
                  }
                }}
                className="w-full px-4 py-3 bg-slate-800/60 border border-slate-700/60 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 transition-smooth"
              >
                {availableSymbols.map(sym => (
                  <option key={sym} value={sym}>{sym}</option>
                ))}
              </select>
            </div>

            {/* Direction */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Direction
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setDirection('long')}
                  className={`flex-1 px-4 py-2.5 rounded-xl font-semibold transition-smooth active:scale-95 focus-ring ${
                    direction === 'long'
                      ? 'bg-emerald-500 text-white shadow-medium shadow-emerald-500/25 border-2 border-emerald-400/50'
                      : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800/80 border border-slate-700/60'
                  }`}
                >
                  <TrendingUp className="w-4 h-4 inline mr-1.5" />
                  LONG
                </button>
                <button
                  onClick={() => setDirection('short')}
                  className={`flex-1 px-4 py-2.5 rounded-xl font-semibold transition-smooth active:scale-95 focus-ring ${
                    direction === 'short'
                      ? 'bg-red-500 text-white shadow-medium shadow-red-500/25 border-2 border-red-400/50'
                      : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800/80 border border-slate-700/60'
                  }`}
                >
                  <TrendingDown className="w-4 h-4 inline mr-1.5" />
                  SHORT
                </button>
              </div>
            </div>

            {/* Entry Price */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Entry Price
              </label>
              <input
                type="number"
                value={entryPrice}
                onChange={(e) => setEntryPrice(parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-3 bg-slate-800/60 border border-slate-700/60 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 transition-smooth"
                placeholder="1.10000"
                step="0.00001"
              />
            </div>

            {/* Stop Loss (Pips) */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Stop Loss (Pips)
              </label>
              <input
                type="number"
                value={stopLossPips}
                onChange={(e) => setStopLossPips(parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-3 bg-slate-800/60 border border-slate-700/60 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 transition-smooth"
                placeholder="20"
                min="1"
                step="1"
              />
              {stopLossPrice > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  Stop Loss Price: {stopLossPrice.toFixed(5)}
                </p>
              )}
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="space-y-4">
            <div className="glass-card p-6 border-2 border-violet-500/40 shadow-medium shadow-violet-500/20">
              <h3 className="text-xl font-bold text-white mb-5 tracking-tight">Risultati Calcolo</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-slate-800/40 rounded-xl border border-slate-700/50">
                  <span className="text-slate-300 font-medium">Risk Amount:</span>
                  <span className="text-2xl font-bold text-red-300 font-mono">
                    ${formatNumber(riskAmount)}
                  </span>
                </div>

                <div className="flex justify-between items-center p-3 bg-slate-800/40 rounded-xl border border-slate-700/50">
                  <span className="text-slate-300 font-medium">Lot Size:</span>
                  <span className="text-2xl font-bold text-violet-300 font-mono">
                    {lotSize > 0 ? lotSize.toFixed(2) : '0.00'} lots
                  </span>
                </div>

                <div className="flex justify-between items-center p-3 bg-slate-800/40 rounded-xl border border-slate-700/50">
                  <span className="text-slate-300 font-medium">Position Size:</span>
                  <span className="text-lg font-bold text-cyan-300 font-mono">
                    {symbol.includes('XAU') 
                      ? `${formatNumber(positionSize, 0)} oz`
                      : symbol.includes('XAG')
                      ? `${formatNumber(positionSize, 0)} oz`
                      : symbol.includes('US30') || symbol.includes('US100') || symbol.includes('US500') || symbol.includes('NAS100') || symbol.includes('SPX')
                      ? `${formatNumber(positionSize, 2)} contracts`
                      : `${formatNumber(positionSize, 0)} units`
                    }
                  </span>
                </div>

                <div className="flex justify-between items-center p-3 bg-slate-800/40 rounded-xl border border-slate-700/50">
                  <span className="text-slate-300 font-medium">Pip Value:</span>
                  <span className="text-lg font-bold text-emerald-300 font-mono">
                    ${formatNumber(pipValue, 2)}/pip
                  </span>
                </div>

                <div className="pt-4 border-t border-slate-700/60">
                  <div className="flex justify-between items-center p-3 bg-slate-800/40 rounded-xl border border-slate-700/50">
                    <span className="text-slate-300 font-medium">Stop Loss Distance:</span>
                    <span className="text-lg font-bold text-white font-mono">
                      {stopLossPips} pips
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-500/15 border-2 border-blue-500/40 rounded-xl p-5 shadow-soft">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg flex-shrink-0">
                  <Info className="w-5 h-5 text-blue-300" />
                </div>
                <div className="text-sm text-blue-200">
                  <p className="font-bold mb-2 text-base">Come usare:</p>
                  <ul className="list-disc list-inside space-y-1.5 text-xs leading-relaxed">
                    <li>Inserisci il tuo account balance</li>
                    <li>Imposta la percentuale di rischio (1-2% raccomandato)</li>
                    <li>Seleziona il simbolo e la direzione</li>
                    <li>Inserisci il prezzo di entrata e lo stop loss in pips</li>
                    <li>Il calcolatore ti mostrerà la dimensione del lotto ottimale</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Warning Box */}
            {riskPercentage > 2 && (
              <div className="bg-amber-500/15 border-2 border-amber-500/40 rounded-xl p-5 shadow-soft">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-500/20 rounded-lg flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-300" />
                  </div>
                  <div className="text-sm text-amber-200">
                    <p className="font-bold text-base">Attenzione!</p>
                    <p className="text-xs mt-2 leading-relaxed">
                      Un rischio superiore al 2% per trade è considerato elevato. 
                      Si raccomanda di mantenere il rischio tra 1-2% per preservare il capitale.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

